import React from 'react';
import { WeezHeader, Card, Form, Message, Button, Icon, SplitContainer, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';
import { useForm, requiredField, requiredEmailField } from '../../../../utils/validators';
import { useHistory } from 'react-router-dom';
import { ticketOfficeContext } from '../../../../components/routing/ticketOfficeRouter';
import Style from './operatorCreation.module.css';

export default function OperatorCreate({ organization, ticket_office }) {
    const { t } = useTranslation();
    const history = useHistory();
    const syncTicketOffice = React.useContext(ticketOfficeContext);
    const toast = React.useContext(ToastContext);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            email: requiredEmailField,
            password: requiredField
        };
    }, []);

    let [form, updateForm, onChangeFormObject, formErrors] = useForm(
        {
            name: '',
            email: '',
            password: ''
        },
        validators
    );

    const saveNewOperator = React.useCallback(() => {
        fetcher
            .post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/users`, {
                data: {
                    name: form.name,
                    email: form.email,
                    password: form.password
                }
            })
            .then(() => {
                toast.success(t('ticket.ticket_offices.operator_creation.request.sucess'));
                onChangeFormObject({ ...form, name: '', email: '', password: '' });
                syncTicketOffice({
                    allow_cache: false,
                    cache: false
                });
                history.push(`/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/users`);
            })
            .catch(() => {
                toast.error(t('ticket.ticket_offices.operator_creation.request.failed'));
            });
    }, [form, organization, ticket_office, toast]);

    const resetForm = React.useCallback(() => {
        onChangeFormObject({ ...form, name: '', email: '', password: '' });
    });

    const isDisabled = React.useMemo(() => {
        return Object.values(form).some(value => !value);
    }, [form]);

    return (
        <>
            <div>
                <WeezHeader
                    backLink={{
                        onClick: () => history.push(`/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/users`)
                    }}
                    title={t('ticket.ticket_offices.operator_creation.general_header_title')}
                    rightComponent={[
                        <Button
                            className={Style['button_cancel']}
                            key="Reset"
                            label={
                                <div>
                                    <Icon name="Reset" inverted={true} />
                                    {t('ticket.common.action.reset')}
                                </div>
                            }
                            onClick={resetForm}
                        />,
                        <Button key="Save" primary label={t('ticket.common.action.save')} onClick={saveNewOperator} disabled={Object.keys(formErrors).length > 0 || isDisabled} />
                    ]}
                    bottomComponent={[<Button primary key={'primary'} label={'Save'} />, <Button key={'secondary'} label={'Cancel'} />]}
                />
            </div>
            <SplitContainer
                additional={
                    <Message mini title={t('ticket.ticket_offices.operator_creation.message.title')} type="tips">
                        {t('ticket.ticket_offices.operator_creation.message.content')}
                    </Message>
                }
            >
                <Card>
                    <Card.Header>
                        <Card.Title title={t('ticket.ticket_offices.operator_creation.card.header.title')} />
                    </Card.Header>
                    <Card.Content>
                        <Form.Field>
                            <Form.Label label={t('ticket.ticket_offices.operator_creation.card.form.name')} />
                            <Form.Input value={form.name} onChange={event => updateForm('name', event.target.value)} errors={Boolean(formErrors['name'])} />
                            <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : ['']} />
                        </Form.Field>
                        <Form.Field>
                            <Form.Label label={t('ticket.ticket_offices.operator_creation.card.form.email')} />
                            <Form.Input value={form.email} onChange={event => updateForm('email', event.target.value)} errors={Boolean(formErrors['email'])} />
                            <Form.FieldErrors errors={formErrors['email'] ? [t(formErrors['email'])] : ['']} />
                        </Form.Field>
                        <Form.Field>
                            <Form.Label label={t('ticket.ticket_offices.operator_creation.card.form.password')} />
                            <Form.Input value={form.password} onChange={event => updateForm('password', event.target.value)} errors={Boolean(formErrors['password'])} />
                            <Form.FieldErrors errors={formErrors['password'] ? [t(formErrors['password'])] : ['']} />
                        </Form.Field>
                    </Card.Content>
                </Card>
            </SplitContainer>
        </>
    );
}
